import React, { useMemo } from 'react';

import {
  Highlight,
  HStack,
  Text,
  VStack,
  type HStackProps,
} from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOptionWithPath, CascaderCommonProps } from '../types';

export interface SearchOptionProps<OptionType extends CascaderOptionWithPath>
  extends HStackProps {
  option: OptionType;
  getCommonProps: () => CascaderCommonProps<OptionType>;
  words: string[];
  lastOptionLabel: string;
  parentPath: string[];
}

const useSearchHighlight = (inputValue: string, path: string[]) => {
  const { parentPath, lastOptionLabel, words } = useMemo(() => {
    const lastOptionLabel = path[path.length - 1];
    const parentPath = path.slice(0, -1);
    const words = inputValue
      .toLowerCase()
      .split(' ')
      .filter((word) => word.length > 0);
    return { parentPath, lastOptionLabel, words };
  }, [path, inputValue]);

  return { parentPath, words, lastOptionLabel };
};

const SearchOptionContainer = <OptionType extends CascaderOptionWithPath>({
  option,
}: {
  option: OptionType;
}) => {
  const {
    handleSelect,
    highlightedValue,
    comboboxApi,
    inputValue,
    cascaderStyles,
    getCommonProps,
  } = useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSearchOption = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SEARCH_OPTION),
    [getComponent],
  );

  const { onClick, ...itemProps } = comboboxApi.getItemProps({
    item: option,
  });
  const joinedPath = option.fullPath.join('/');
  const isHighlighted = joinedPath === highlightedValue;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick?.(e);
    handleSelect(option, option.fullPath);
  };

  const searchHighlight = useSearchHighlight(inputValue, option.fullLabels);

  return (
    <CustomSearchOption
      option={option}
      getCommonProps={getCommonProps}
      __css={cascaderStyles.searchOption}
      {...itemProps}
      data-highlighted={isHighlighted || undefined}
      onClick={handleClick}
      aria-selected={isHighlighted}
      {...searchHighlight}
      className="cascader-search-option"
    />
  );
};

export const InternalSearchOption = <
  OptionType extends CascaderOptionWithPath,
>({
  index,
}: {
  index: number;
}) => {
  const { filteredOptions } = useCascaderState<OptionType>();
  const option = filteredOptions[index];
  if (!option) {
    return null;
  }
  return <SearchOptionContainer option={option} />;
};

export const SearchOptionPath = ({
  path,
  words,
}: {
  path: string[];
  words: string[];
}) => (
  <HStack>
    {path.length > 0 && (
      <Text size="sm" color="text.secondary" as="span" noOfLines={1}>
        {path.map((label, index) => {
          return index === 0 ? (
            <Highlight key={label} query={words}>
              {label}
            </Highlight>
          ) : (
            [
              ' / ',
              <Highlight key={label} query={words}>
                {label}
              </Highlight>,
            ]
          );
        })}
      </Text>
    )}
  </HStack>
);

export const SearchOptionLabel = ({
  label,
  words,
}: {
  label: string;
  words: string[];
}) => (
  <Text size="md" as="span" textAlign="left">
    {words.length > 0 ? <Highlight query={words}>{label}</Highlight> : label}
  </Text>
);

export const SearchOption = <OptionType extends CascaderOptionWithPath>({
  option,
  getCommonProps: _,
  words,
  lastOptionLabel,
  parentPath,
  ...rest
}: SearchOptionProps<OptionType>) => {
  return (
    <HStack {...rest}>
      <VStack flexGrow={1} align="start" gap={0}>
        <SearchOptionLabel label={lastOptionLabel} words={words} />
        <SearchOptionPath path={parentPath} words={words} />
      </VStack>
    </HStack>
  );
};
